import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Leaf, CheckCircle, MapPin, Calendar, Clock, Car, CreditCard, ArrowRight, Loader2, XCircle, MessageCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const WHATSAPP_PHONE = '+33753202093';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ConfirmationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeader } = useAuth();
  const { t } = useTranslation();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const cancelled = searchParams.get('cancelled');

  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId) {
        navigate('/dashboard');
        return;
      }

      try {
        const response = await axios.get(`${API}/bookings/${bookingId}`, {
          headers: getAuthHeader()
        });
        setBooking(response.data);
      } catch (error) {
        console.error('Error fetching booking:', error);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [bookingId, getAuthHeader, navigate]);

  // Poll payment status if session_id is present
  useEffect(() => {
    if (!sessionId || pollCount >= 10) return;

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`, {
          headers: getAuthHeader()
        });
        
        if (response.data.payment_status === 'paid') {
          setPaymentStatus('success');
          // Refresh booking to get updated status
          if (bookingId) {
            const bookingResponse = await axios.get(`${API}/bookings/${bookingId}`, {
              headers: getAuthHeader()
            });
            setBooking(bookingResponse.data);
          }
        } else if (response.data.status === 'expired') {
          setPaymentStatus('failed');
        } else {
          setPollCount(prev => prev + 1);
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        setPollCount(prev => prev + 1);
      }
    };

    const timer = setTimeout(pollPaymentStatus, 2000);
    return () => clearTimeout(timer);
  }, [sessionId, pollCount, bookingId, getAuthHeader]);

  // Google Ads conversion tracking
  const isPaymentSuccess = paymentStatus === 'success' || (booking?.payment_status === 'paid');
  const isOnboardPayment = booking?.payment_method === 'cash' || booking?.payment_method === 'card_onboard';
  useEffect(() => {
    if (booking && (isPaymentSuccess || isOnboardPayment)) {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17986670215/B1EqCO7QpYEcEIed24BD',
          'transaction_id': booking.id || bookingId || '',
        });
      }
    }
  }, [booking, isPaymentSuccess, isOnboardPayment, bookingId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="min-h-screen bg-[#09090b]">
        <header className="px-6 py-6 border-b border-zinc-800">
          <div className="max-w-4xl mx-auto">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-14 h-14" />
              <span className="font-bold text-xl font-['Outfit'] text-white">YOUMI GREEN</span>
              <span className="text-emerald-500 font-bold text-xl font-['Outfit']">VTC</span>
            </button>
          </div>
        </header>

        <main className="px-6 py-16">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
              {t('confirm_cancelled')}
            </h1>
            <p className="text-zinc-400 mb-8">
              {t('confirm_cancelled_desc')}
            </p>
            <div className="flex flex-col gap-3">
              <Button 
                className="btn-primary"
                onClick={() => navigate('/booking')}
                data-testid="retry-booking-btn"
              >
                {t('confirm_retry')}
              </Button>
              <Button 
                variant="ghost"
                className="text-zinc-400 hover:text-white"
                onClick={() => navigate('/dashboard')}
              >
                {t('confirm_view_bookings')}
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const isPaymentPending = sessionId && paymentStatus === 'checking' && pollCount < 10;

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="px-6 py-6 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-14 h-14" />
            <span className="font-bold text-xl font-['Outfit'] text-white">YOUMI GREEN</span>
            <span className="text-emerald-500 font-bold text-xl font-['Outfit']">VTC</span>
          </button>
        </div>
      </header>

      <main className="px-6 py-16">
        <div className="max-w-2xl mx-auto">
          {/* Status */}
          <div className="text-center mb-12">
            {isPaymentPending ? (
              <>
                <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
                </div>
                <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
                  {t('confirm_checking')}
                </h1>
                <p className="text-zinc-400">
                  {t('confirm_wait')}
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-green">
                  <CheckCircle className="w-10 h-10 text-emerald-500" />
                </div>
                <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
                  {t('confirm_success')}
                </h1>
                <p className="text-zinc-400">
                  {isOnboardPayment 
                    ? t('confirm_success_onboard')
                    : t('confirm_success_online')}
                </p>
              </>
            )}
          </div>

          {/* Booking Details */}
          {booking && (
            <div className="glass-card rounded-2xl p-8">
              <h2 className="text-xl font-bold text-white font-['Outfit'] mb-6">
                {t('confirm_details')}
              </h2>

              <div className="space-y-6">
                {/* Route */}
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                    <div className="w-0.5 h-16 bg-zinc-700" />
                    <div className="w-3 h-3 bg-red-500 rounded-full" />
                  </div>
                  <div className="flex-1 space-y-4">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{t('confirm_departure')}</p>
                      <p className="text-white">{booking.pickup_address}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{t('confirm_arrival')}</p>
                      <p className="text-white">{booking.destination_address}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-zinc-800 pt-6 grid grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="text-xs text-zinc-500">{t('confirm_date')}</p>
                      <p className="text-white">
                        {format(parseISO(booking.pickup_datetime), 'PPP', { locale: fr })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="text-xs text-zinc-500">{t('confirm_time')}</p>
                      <p className="text-white">
                        {format(parseISO(booking.pickup_datetime), 'HH:mm')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Car className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="text-xs text-zinc-500">Distance</p>
                      <p className="text-white">{booking.distance_km.toFixed(1)} km</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="text-xs text-zinc-500">{t('booking_step_payment')}</p>
                      <p className="text-white">
                        {booking.payment_method === 'stripe' && t('booking_card')}
                        {booking.payment_method === 'card_onboard' && t('booking_card_onboard')}
                        {booking.payment_method === 'cash' && t('booking_cash')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-zinc-800 pt-6">
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-300 text-lg">Total</span>
                    <span className="text-3xl font-bold text-emerald-500 font-['Outfit']">
                      {booking.price.toFixed(2)}€
                    </span>
                  </div>
                  {isPaymentSuccess && (
                    <div className="mt-2 flex items-center gap-2 text-emerald-400 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      {t('confirm_paid')}
                    </div>
                  )}
                  {isOnboardPayment && (
                    <div className="mt-2 text-zinc-400 text-sm">
                      {t('confirm_pay_driver')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <Button 
              className="btn-primary flex-1"
              onClick={() => navigate('/dashboard')}
              data-testid="view-bookings-btn"
            >
              {t('confirm_view_bookings')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button 
              variant="outline"
              className="flex-1 border-zinc-700 text-white hover:bg-zinc-800"
              onClick={() => navigate('/booking')}
              data-testid="new-booking-btn"
            >
              {t('confirm_retry')}
            </Button>
          </div>

          {/* WhatsApp Contact */}
          {booking && (
            <div className="mt-8 glass-card rounded-2xl p-6 text-center">
              <h3 className="text-lg font-semibold text-white mb-3">
                Une question ?
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                Contactez-nous directement sur WhatsApp pour toute demande
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a 
                  href={`https://wa.me/33753202093?text=${encodeURIComponent(`Bonjour YOUMI GREEN VTC,\n\nConcernant ma réservation #${booking.id?.slice(0,8)}...\n\n📅 ${format(parseISO(booking.pickup_datetime), 'PPP', { locale: fr })}\n🕐 ${format(parseISO(booking.pickup_datetime), 'HH:mm')}\n📍 ${booking.pickup_address}\n🏁 ${booking.destination_address}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#1ebe5a] text-white rounded-full font-medium transition-colors"
                  data-testid="whatsapp-contact-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp
                </a>
                <a 
                  href="tel:+33753202093"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-700 hover:bg-zinc-600 text-white rounded-full font-medium transition-colors"
                  data-testid="phone-contact-btn"
                >
                  <Phone className="w-5 h-5" />
                  +33 7 53 20 20 93
                </a>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
